import { useCallback, memo } from "react";
import { useAddToNiche } from "queries";
import { AddToNichePopup } from "./AddToNichePopup";
import { IErrorData } from "services/types";
import { compareOptions } from "./utils/helpers";
import { useNavigate } from "react-router-dom";
import { routes } from "routes";
import { toast } from "react-hot-toast";
import { ALLOWED_NICHES, MAX_NICHES_MESSAGE, NOT_AVAILABLE_MESSAGE, ERROR_SAVE_NICHES } from "./utils/constants";
import { AxiosError } from "axios";
import { useNicheData } from "./utils/useNicheData";
import "./styles/AddToNichePopup.scss";

interface IAddToNichePopupProps {
  isOpen: boolean;
  togglePopup: () => void;
  gifId: string;
}

export const AddToNichePopupContainer = memo((props: IAddToNichePopupProps) => {
  const { gifId, togglePopup, isOpen } = props;
  const { nicheFields, error, handleSetNicheFiled } = useNicheData(gifId);
  const addToNiche = useAddToNiche();
  const navigate = useNavigate();

  const handleCheckBoxChange = useCallback((indx: number) => {
    const newCheckboxes = [...nicheFields];
    const checked = newCheckboxes.filter(niche => niche.isChecked);

    if (checked.length === ALLOWED_NICHES && !newCheckboxes[indx].isChecked) {
      toast.error(MAX_NICHES_MESSAGE, { duration: 5000 });
      return;
    }

    let indxs = 0;

    newCheckboxes.forEach((el, indx) => {
      if (el.isChecked) {
        indxs = indx;
      }
    });

    newCheckboxes[indx].isChecked = !newCheckboxes[indx].isChecked;

    if (newCheckboxes[indx].isChecked) {
      const element = newCheckboxes.splice(indx, 1)[0];
      newCheckboxes.splice(indxs + 1, 0, element);
    }

    handleSetNicheFiled(newCheckboxes.sort(compareOptions));
  }, [nicheFields, handleSetNicheFiled]);

  const handleSubmit = useCallback(async () => {
    const updateNichesQty = nicheFields.filter(niche => niche.isChecked && !niche.disabled);
    const data = nicheFields
      .filter(niche => niche.isChecked)
      .map((niche) => niche.value);

    try {
      await addToNiche.mutateAsync({
        gifId: gifId,
        nicheIds: data
      });
      togglePopup();
      toast.success(`Successfully Added to ${updateNichesQty.length} Niche${updateNichesQty.length > 1 ? "s" : ""}.`, { duration: 5000 });
    } catch (err) {
      console.error("Could not set gif niches", err);
      toast.error(
        (err as AxiosError<IErrorData>).response?.data?.error?.message ||
        ERROR_SAVE_NICHES,
        { duration: 5000 }
      );
    }

  }, [addToNiche, gifId, nicheFields, togglePopup]);

  const handleExploreNiches = useCallback(() => {
    navigate(routes.niches());
  }, [navigate]);

  const handleAvailableMessage = useCallback(() => {
    toast.error(NOT_AVAILABLE_MESSAGE);
  }, []);

  const handleChange = useCallback((indx: number, available: boolean = true) => {
    if (!available) {
      handleAvailableMessage();
    } else {
      handleCheckBoxChange(indx);
    }
  }, [handleAvailableMessage, handleCheckBoxChange]);

  const handleBtnClick = useCallback(() => {
    if (nicheFields.length > 0) {
      handleSubmit();
    } else {
      handleExploreNiches();
      togglePopup();
    }
  }, [nicheFields, handleSubmit, handleExploreNiches, togglePopup]);

  return (
    <AddToNichePopup
      isError={error}
      niches={nicheFields}
      onChange={handleChange}
      onBtnClick={handleBtnClick}
      isOpen={isOpen}
      togglePopup={togglePopup}
    />
  );
});
